// The workbench's files (RFC-057 model 6): the machine's work tree a folder
// at a time, each opened as it is unfolded and listed again when the page
// says the tree changed (`refresh`). A file is opened with a click; each
// row's ⋯ renames or deletes it; the head makes a file or a folder in the
// folder picked last, or the tree's top.
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { t } from "../i18n";
import type { Entry } from "../workbench";
import { FileIcon } from "./FileIcon";
import { RowMenu } from "./RowMenu";

export function FileTree({
  root,
  list,
  refresh,
  open,
  onOpen,
  onNew,
  onRename,
  onDelete,
  onWorkHere,
  onRefresh,
  names,
  kept,
}: {
  /** the tree's top as a person reads it (~/work) */
  root: string;
  list: (path: string) => Promise<Entry[]>;
  /** counted up when the tree may have changed: every unfolded folder is listed again */
  refresh: number;
  /** the file shown in the editor */
  open?: string;
  onOpen: (path: string) => void;
  onNew: (folder: string, kind: "file" | "dir") => void;
  onRename: (e: Entry) => void;
  onDelete: (e: Entry) => void;
  /** a folder made the thread's (RFC-057 D8) */
  onWorkHere: (e: Entry) => void;
  /** the tree, and what the page finds in it, looked at again */
  onRefresh: () => void;
  /**
   * a folder's name as a person reads it, by its path, its own kept as the
   * row's tooltip and description: an exam's folder by the paper's title,
   * not the assignment's id (byj-1wv.118.20). The path, and what the folder
   * is called on the machine, are unchanged.
   */
  names?: Record<string, string>;
  /**
   * whether the page keeps an entry, by its path, offered no rename or
   * delete: an exam's attempt, its record and everything in its .exam, and
   * its questions' folders, which renamed or deleted are no exam
   * (byj-1wv.118.20). Named or not.
   */
  kept?: (path: string) => boolean;
}) {
  const [folders, setFolders] = useState<Record<string, Entry[] | "loading" | { error: string }>>({});
  const [unfolded, setUnfolded] = useState<Set<string>>(() => new Set([""]));
  const [picked, setPicked] = useState("");

  const load = useCallback(
    (path: string) => {
      list(path).then(
        (entries) => setFolders((f) => ({ ...f, [path]: entries })),
        (err: unknown) => setFolders((f) => ({ ...f, [path]: { error: err instanceof Error ? err.message : String(err) } })),
      );
    },
    [list],
  );

  // every unfolded folder, again whenever the tree may have changed
  useEffect(() => {
    for (const p of unfolded) load(p);
    // the folders unfolded since are loaded as they unfold
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, load]);

  const toggle = (path: string) => {
    setPicked(path);
    setUnfolded((u) => {
      const next = new Set(u);
      if (next.has(path)) next.delete(path);
      else {
        next.add(path);
        if (folders[path] === undefined) {
          setFolders((f) => ({ ...f, [path]: "loading" }));
          load(path);
        }
      }
      return next;
    });
  };

  const rows = (path: string, depth: number): ReactNode => {
    const f = folders[path];
    if (f === undefined || f === "loading") {
      return (
        <li className="muted wb-tree-note" style={{ paddingLeft: `${depth * 12 + 8}px` }}>
          {t("common:loading")}
        </li>
      );
    }
    if (!Array.isArray(f)) {
      return (
        <li className="error wb-tree-note" style={{ paddingLeft: `${depth * 12 + 8}px` }}>
          {f.error}
        </li>
      );
    }
    if (f.length === 0 && path !== "") {
      return (
        <li className="muted wb-tree-note" style={{ paddingLeft: `${depth * 12 + 8}px` }}>
          {t("workbench:emptyFolder")}
        </li>
      );
    }
    return f.map((e) => (
      <li key={e.path}>
        <div className={`wb-tree-row${e.path === open ? " wb-tree-open" : ""}`} style={{ paddingLeft: `${depth * 12 + 4}px` }}>
          {e.kind === "dir" ? (
            <button
              className="wb-tree-item"
              aria-expanded={unfolded.has(e.path)}
              // the folder's own name, on hover and to a screen reader, where the page names it otherwise
              title={names?.[e.path] ? e.name : undefined}
              onClick={() => toggle(e.path)}
            >
              <span className="wb-tree-mark" aria-hidden="true" />
              <FileIcon name={e.name} dir open={unfolded.has(e.path)} />
              {names?.[e.path] ?? e.name}
            </button>
          ) : e.kind === "link" ? (
            <span className="wb-tree-item muted" title={t("workbench:linkOutside")}>
              <span className="wb-tree-mark wb-tree-link" aria-hidden="true">
                ↗
              </span>
              <FileIcon name={e.name} />
              {e.name}
            </span>
          ) : (
            <button
              className="wb-tree-item"
              aria-current={e.path === open ? "true" : undefined}
              onClick={() => {
                setPicked(e.path.includes("/") ? e.path.slice(0, e.path.lastIndexOf("/")) : "");
                onOpen(e.path);
              }}
            >
              <span className="wb-tree-mark wb-tree-leaf" aria-hidden="true" />
              <FileIcon name={e.name} />
              {e.name}
            </button>
          )}
          <RowMenu
            label={t("workbench:fileActions", { name: names?.[e.path] ?? e.name })}
            items={[
              ...(e.kind === "dir" ? [{ label: t("workbench:workHere"), onSelect: () => onWorkHere(e) }] : []),
              // a folder the page keeps is the page's — an exam's attempt or
              // a question's, which renamed or deleted is no exam
              ...(kept?.(e.path)
                ? []
                : [
                    { label: t("workbench:rename"), onSelect: () => onRename(e) },
                    { label: t("workbench:delete"), onSelect: () => onDelete(e), danger: true },
                  ]),
            ]}
          />
        </div>
        {e.kind === "dir" && unfolded.has(e.path) && <ul className="wb-tree-list">{rows(e.path, depth + 1)}</ul>}
      </li>
    ));
  };

  return (
    <nav className="wb-files" aria-label={t("workbench:files")}>
      {/* the section's name; the work tree's path is the page head's, and on hover here */}
      <div className="wb-tree-head">
        <span className="wb-tree-title" title={root}>
          {t("workbench:files")}
        </span>
        <span className="grow" />
        {/* a page and a folder each with a plus, told apart at a glance; their names on hover and to a screen reader */}
        <button className="small ghost wb-tree-tool" onClick={() => onNew(picked, "file")} aria-label={t("workbench:newFile")} title={t("workbench:newFile")}>
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
            <path d="M9 1.75H4.25a1 1 0 0 0-1 1v10.5a1 1 0 0 0 1 1h7.5a1 1 0 0 0 1-1V5.5Z M9 1.75V5.5h3.75" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
            <path d="M8 7.5v4.5M5.75 9.75h4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        </button>
        <button className="small ghost wb-tree-tool" onClick={() => onNew(picked, "dir")} aria-label={t("workbench:newFolder")} title={t("workbench:newFolder")}>
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
            <path d="M1.75 4a1 1 0 0 1 1-1h3.1l1.4 1.5h6a1 1 0 0 1 1 1v6.75a1 1 0 0 1-1 1H2.75a1 1 0 0 1-1-1Z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
            <path d="M8 7v4.25M5.9 9.1h4.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        </button>
        <button className="small ghost wb-tree-tool" onClick={onRefresh} aria-label={t("workbench:refresh")} title={t("workbench:refresh")}>
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
            <path d="M13.25 8A5.25 5.25 0 1 1 11.6 4.2" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            <path d="M12.25 1.75v2.9h-2.9" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      <ul className="wb-tree-list">{rows("", 0)}</ul>
    </nav>
  );
}
